import { useState, useEffect, useCallback, useRef } from 'react';
import { getJobAssignmentsOverview, getJobAssignmentsFilterOptions } from '../api/recruiter';

interface JobAssignment {
    assignment_id: string;
    job_id: string;
    job_title: string;
    job_status: string;
    job_department: string;
    job_company_name: string;
    recruiter_id: string;
    recruiter_name: string;
    recruiter_email: string;
    recruiter_company_name: string;
    recruiter_company_image: string;
    assignment_date: string;
    is_active: boolean;
    fee_type: string;
    fee_description: string;
    current_target_type: string;
    current_target_value: number;
    assignment_count: number;
}

interface PaginationInfo {
    count: number;
    next: number | null;
    previous: number | null;
    page_size: number;
    current_page: number;
    total_pages: number;
}

interface FilterOptions {
    recruiter_companies: string[];
    job_titles: string[];
    client_companies: string[];
}

interface Filters {
    search: string;
    status: string;
    recruiter_company: string;
    client_company: string;
    job_title: string;
    sort: string;
}

export const useJobAssignments = () => {
    const [loading, setLoading] = useState(true);
    const [filterOptionsLoading, setFilterOptionsLoading] = useState(true);
    const [assignments, setAssignments] = useState<JobAssignment[]>([]);
    const [pagination, setPagination] = useState<PaginationInfo | null>(null);
    const [filterOptions, setFilterOptions] = useState<FilterOptions>({
        recruiter_companies: [],
        job_titles: [],
        client_companies: []
    });
    const [filters, setFilters] = useState<Filters>({
        search: '',
        status: '',
        recruiter_company: '',
        client_company: '',
        job_title: '',
        sort: '-assignment_date',
    });

    const fetchAssignments = useCallback(async (filtersToUse: Filters, page = 1) => {
        try {
            setLoading(true);
            const response = await getJobAssignmentsOverview({
                ...filtersToUse,
                page,
            });
            setAssignments(response.data.results);
            setPagination(response.data.pagination);
        } catch (error) {
            console.error('Error fetching job assignments:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchFilterOptions = useCallback(async () => {
        try {
            setFilterOptionsLoading(true);
            const response = await getJobAssignmentsFilterOptions();
            setFilterOptions(response.data);
        } catch (error) {
            console.error('Error fetching filter options:', error);
        } finally {
            setFilterOptionsLoading(false);
        }
    }, []);

    const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const debouncedFetch = useCallback((filtersToUse: Filters) => {
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }
        debounceTimeoutRef.current = setTimeout(() => {
            fetchAssignments(filtersToUse, 1);
        }, 300);
    }, [fetchAssignments]);

    useEffect(() => {
        fetchFilterOptions();
        fetchAssignments(filters);
        // Only run on component mount
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Cleanup debounce timeout on unmount
    useEffect(() => {
        return () => {
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }
        };
    }, []);

    const handleFormChange = (_: any, allValues: any) => {
        const newFilters = {
            search: allValues.search || '',
            status: allValues.status || '',
            recruiter_company: allValues.recruiter_company || '',
            client_company: allValues.client_company || '',
            job_title: allValues.job_title || '',
            sort: filters.sort,
        };

        setFilters(newFilters);
        debouncedFetch(newFilters);
    };

    const handleSort = (sortField: string) => {
        const newSort = filters.sort === sortField ? `-${sortField}` : sortField;
        const newFilters = {
            ...filters,
            sort: newSort,
        };

        setFilters(newFilters);
        fetchAssignments(newFilters, 1);
    };

    const handlePageChange = (page: number) => {
        fetchAssignments(filters, page);
    };

    return {
        loading,
        filterOptionsLoading,
        assignments,
        pagination,
        filterOptions,
        filters,
        handleFormChange,
        handleSort,
        handlePageChange,
    };
};

export type { JobAssignment, PaginationInfo, FilterOptions, Filters }; 